import { argumentString, isUnlimitedUserCredit } from "./utils";
import { getBrandValue } from "../brand/selectors";
import { favoritesEnabled } from "../favorites/selectors";
import { createSelector } from "reselect";
import IntlUtil from "../../utils/IntlUtils";
import { intersection, objectToArray } from "../../utils/array-utils";
import moment from "moment";
import * as utils from "./utils";
import { PAID_SUBSCRIPTION } from "../../ui/apps/library/promotions-lane/promotionConsts";

export const ADOPTIONS = "adoptions";
export const GROUPS = "groups";
export const ASSETS = "assets";

export const shouldNextPageFetch = (state, key, args) => {
  const argString = argumentString(args);
  const results = state.bridge[key].results[argString];
  if (results && results.pages) {
    const pages = results.pages;
    const totalPages = results.totalPages;
    let pagesFetched = 0;
    let pagesLoading = 0;
    Object.keys(pages).forEach(p => {
      if (pages[p].isFetching) {
        pagesLoading += 1;
      } else {
        pagesFetched += 1;
      }
    });
    return !pagesLoading && totalPages > pagesFetched;
  }
};

export const getMaxLoadedOrLoadingPage = (state, key, args) => {
  const argString = argumentString(args);
  const results = state.bridge[key].results[argString];
  if (results && results.pages) {
    const pages = results.pages;
    return Object.keys(pages).reduce((acc, cv) => Math.max(acc, cv), 0);
  }
  return 0;
};

const isFetchingGeneric = (key, state, args) => {
  const argString = argumentString(args);
  const results = state.bridge[key].results[argString] || {};
  const pages = results.pages || {};
  const p = args.page || 0;
  const isFetching = !!(pages[p] && pages[p].isFetching);

  return isFetching;
};

export const isAdoptionsFetching = (state, args) => {
  return isFetchingGeneric(ADOPTIONS, state, args);
};

export const isGroupsFetching = (state, args) => {
  return isFetchingGeneric(GROUPS, state, args);
};

export const isAssetsFetching = (state, args) => {
  return isFetchingGeneric(ASSETS, state, args);
};

const getAllGeneric = (key, state, args) => {
  const argString = argumentString(args);
  const results = state.bridge[key].results[argString] || {};
  const pages = results.pages;
  if (pages) {
    return Object.keys(pages).reduce((acc, p) => {
      const page = pages[p];
      if (page && !page.isFetching) {
        return acc.concat(page.content.map(id => state.bridge[key].byId[id]));
      }
      return acc;
    }, []);
  } else {
    return [];
  }
};

const getFiltersData = (key, state, args) => {
  const argString = argumentString(args);
  const metaData = state.bridge[key].results[argString] || {};
  const filters =
    metaData.filters &&
    metaData.filters[0] &&
    metaData.filters[0].split(".")[1];
  return { status: metaData.status, filters: filters };
};

export const getSubjectFilters = (state, args) =>
  getFiltersData(ASSETS, state, args);

export const getGroups = (state, args) => getAllGeneric(GROUPS, state, args);

export const getAdoptions = (state, args) =>
  getAllGeneric(ADOPTIONS, state, args);

export const getAssets = (state, args) => getAllGeneric(ASSETS, state, args);

export const getHasActiveReturnableAsset = state => {
  const assets = Object.values(state.bridge.assets.byId);
  let match = assets.find(function (element) {
    return element.active && element.returnable;
  });
  return match ? true : false;
};

/**
 // * @return array All adoptions that have more than 0 assets or are still trying to figure out how many assets they have
 */
export const getNonEmptyAdoptions = createSelector(
  state => getAdoptions(state),
  state => state.bridge[ASSETS].results,
  (adoptions, adoptionAssetData) => {
    return adoptions.filter(a => {
      const argStr = argumentString({ adoption: a.id });
      const assetData = adoptionAssetData[argStr];
      return (
        !assetData ||
        assetData.pages[0].isFetching ||
        assetData.totalElements > 0
      );
    });
  }
);

const getTotalItemsGeneric = (key, state, args) => {
  const argString = argumentString(args);
  const results = state.bridge[key].results[argString];
  if (results) {
    return results.totalElements;
  }
  return -1;
};

export const getTotalAssets = (state, args) =>
  getTotalItemsGeneric(ASSETS, state, args);

export const getAsset = (state, isbn) => {
  const asset = state.bridge.assets.byId[isbn];
  if (favoritesEnabled(state)) {
    asset.favorite =
      state && state.favorites && state.favorites.favorites
        ? state.favorites.favorites[isbn]
        : undefined;
  }

  // Make sure it's not just an empty isFetching or something else
  return asset && asset.vbid ? asset : undefined;
};

export const isActive = (state, isbn) => {
  const asset = getAsset(state, isbn);
  if (asset && asset.active) {
    return true;
  }
  return false;
};

export const hasAvailableAssetCredits = (state, isbn) => {
  return getAvailableAssetCredits(state, isbn).length > 0;
};

export const getExpirationTimestamp = (state, isbn) => {
  const asset = getAsset(state, isbn);
  if (
    asset === undefined ||
    !asset.expirationDate ||
    asset.expirationDate === "never"
  ) {
    return "";
  }
  return asset.expirationDate;
};

export const getPublishDate = (state, isbn) => {
  const asset = getAsset(state, isbn);
  if (
    asset === undefined ||
    asset.publishDate === undefined ||
    asset.publishDate === ""
  ) {
    return "";
  }
  const publishDateValid = moment(asset.publishDate).isValid();
  if (publishDateValid) {
    return IntlUtil(new Date(asset.publishDate), "en-US");
  }
  return asset.publishDate;
};

export const getGroupNameForAdoption = (state, id) => {
  const adoption = state.bridge.adoptions.byId[id];
  if (adoption) {
    const group = state.bridge.groups.byId[adoption.group];
    if (group) {
      return group.name;
    }
  }
  return "";
};

export const getAdoptionName = (state, id) => {
  const adoption = state.bridge.adoptions.byId[id];
  if (adoption) {
    return adoption.name;
  }
  return "";
};

export const getUser = state => state.bridge.user;
export const isUserFetching = state => state.bridge.user.isFetching;
export const isBridgeSessionReady = state => state.bridge.user.status === 200;

export const hasAdmin = state => getUser(state).hasAdmin;
export const hasGroups = state => getUser(state).hasGroups;
export const hasSubscriptions = state => getUser(state).hasAssets;
export const hasInteractives = state => getUser(state).hasInteractives;
export const isGlobalOfferFlowEnabled = state => getUser(state).globalOfferFlow;

export const arePromotionsFetching = state =>
  state.bridge && state.bridge.promotions && state.bridge.promotions.isFetching;

const filterPromotions = (state, promos = []) => {
  if (!getBrandValue(state, "ShortcutLane.enabled")) {
    promos = [];
  }
  if (!getBrandValue(state, "ShortcutLane.Promotions.enabled")) {
    promos = promos.filter(p => p.type === PAID_SUBSCRIPTION);
  }
  const sortByActiveStatus = (x, y) => {
    return x.active === y.active ? 0 : x.active ? -1 : 1;
  };
  return promos.sort(sortByActiveStatus);
};

export const getPromotions = (state, allPromos = true) => {
  const shortcuts = [];
  if (!isUserFetching(state) && allPromos) {
    const creditList = getUser(state).creditList;
    if (creditList) {
      objectToArray(creditList).forEach(credit => {
        if (credit.type === "S") {
          const brand = getSubscriptionBranding(state, credit.vbid);
          if (brand) {
            shortcuts.push({
              active: true,
              cover: brand.image,
              description: brand.description,
              title: brand.title,
              type: PAID_SUBSCRIPTION,
              value: credit.vbid
            });
          }
        }
      });
    }
  }
  objectToArray(getPromotionsAsObject(state)).forEach(promo =>
    shortcuts.push(promo)
  );

  return filterPromotions(state, shortcuts);
};

export const isActivating = (state, isbn) => {
  const asset = getAsset(state, isbn);
  return asset ? asset.activating : false;
};

export const isReturnable = (state, isbn) => {
  const asset = getAsset(state, isbn);
  return asset ? asset.returnable : false;
};

export const isBridgeUser = state =>
  !!(
    (getUser(state).bridges && getUser(state).bridges.length > 0) ||
    getUser(state).hasAssets
  );
export const isTeacher = state => isBridgeUser(state) && !getUser(state).level;

export const shouldShowTeacherPortal = state =>
  isTeacher(state) && getBrandValue(state, "TeacherPortal.enabled", false);

export const getUserCreditsById = state => {
  if (!isUserFetching(state)) {
    const creditList = getUser(state).creditList;
    return creditList || {};
  }
  return {};
};
export const getUserCredits = state => {
  if (!isUserFetching(state)) {
    const creditList = getUser(state).creditList;
    return creditList ? Object.values(creditList) : [];
  }
  return [];
};
export const allUserCreditsAreUnlimited = state => {
  const creditList = getUserCredits(state);
  return creditList.every(utils.isUnlimitedUserCredit);
};
export const anyUserCreditsAreUnlimited = state => {
  const creditList = getUserCredits(state);
  return Boolean(creditList.find(utils.isUnlimitedUserCredit));
};
export const hasAnyReturnableOrLimitedUserCredits = state => {
  const creditList = getUserCredits(state);
  return (
    creditList.length > 0 &&
    !!creditList.find(c => c.returnable || !isUnlimitedUserCredit(c))
  );
};
export const getAssetCredits = (state, isbn) => {
  const asset = getAsset(state, isbn);
  return (asset && asset.creditList) || [];
};
export const getCreditCount = state => {
  const creditList = getUserCredits(state);
  return creditList.length || 0;
};
export const getUserSubscriptionVbids = state => {
  const creditList = getUserCredits(state);
  let subscriptions = [];
  creditList.forEach(credit => {
    if (credit.type === "S") {
      subscriptions.push(credit.vbid);
    } else if (credit.type === "E") {
      subscriptions.push(credit.subscriptionVbid);
    } else {
      if (credit.subscriptions) {
        subscriptions.push(...credit.subscriptions);
      }
    }
  });
  return subscriptions;
};
export const isBrandedSubscription = (state, subscriptionVbid) => {
  const branding = getSubscriptionBranding(state, subscriptionVbid);
  return branding !== undefined;
};
export const hasSingleBrandedSubscription = state => {
  const subscriptions = getUserSubscriptionVbids(state);
  if (subscriptions.length !== 1) {
    return false;
  }
  return isBrandedSubscription(state, subscriptions[0]);
};

export const getActivePromotions = state =>
  getBrandValue(state, "ShortcutLane.enabled")
    ? getPromotions(state, false).filter(p => p.active)
    : [];

export const getPromotionName = (state, subscriptionVbid) => {
  const promotion = getPromotionsAsObject(state)[subscriptionVbid];
  return promotion && promotion.title;
};

export const getPromotionsAsObject = state => {
  if (!isUserFetching(state)) {
    if (
      state.bridge &&
      state.bridge.promotions &&
      state.bridge.promotions.promotions
    ) {
      return state.bridge.promotions.promotions;
    }
  }
  return {};
};

export const getSubscriptionBranding = (state, subscriptionVbid) => {
  if (!isUserFetching(state)) {
    try {
      return getUser(state).branding[subscriptionVbid];
    } catch (e) {
      return undefined;
    }
  }
  return undefined;
};
export const getSubscriptionImage = (state, subscriptionVbid) => {
  try {
    return getSubscriptionBranding(state, subscriptionVbid).image;
  } catch (e) {
    return undefined;
  }
};
export const getSubscriptionName = (state, subscriptionVbid) => {
  try {
    return getSubscriptionBranding(state, subscriptionVbid).title;
  } catch (e) {
    return undefined;
  }
};
export const getSubscriptionDescription = (state, subscriptionVbid) => {
  try {
    return getSubscriptionBranding(state, subscriptionVbid).description;
  } catch (e) {
    return undefined;
  }
};
export const getSubscriptionCategoryLanes = (state, subscriptionVbid) => {
  try {
    return getSubscriptionBranding(state, subscriptionVbid).lanes;
  } catch (e) {
    return undefined;
  }
};
/**
 * Returns the custom display mode for a subscription, and undefined if one is not set
 */
export const getSubscriptionBrandDisplayMode = (state, subscriptionVbid) => {
  const brandedSubscription = getSubscriptionBranding(state, subscriptionVbid);
  if (brandedSubscription && brandedSubscription.displayMode) {
    return brandedSubscription.displayMode;
  }
  return undefined;
};
/**
 * Returns the display mode for a subscription - will first check the custom value,
 * if that is not set it will be "GRID" if <= 20 results, and "LANES" for >20 results.
 * The subscription assets must be loaded.
 */
export const getSubscriptionDisplayMode = (state, subscriptionVbid) => {
  try {
    const brandedSubscription = getSubscriptionBranding(
      state,
      subscriptionVbid
    );
    if (brandedSubscription && brandedSubscription.displayMode) {
      return brandedSubscription.displayMode;
    }
    const assets = getAssets(
      state,
      utils.formatBrandedSubscriptionParams(subscriptionVbid)
    );
    return assets.length > 20 ? "LANES" : "GRID";
  } catch (e) {
    return undefined;
  }
};
/**
 * Returns the display mode if there is one single subscription on the user's account.
 * Will first check the custom value, then the total number of assets.
 * The total assets must be loaded.
 */
export const getSingleBrandedSubscriptionMode = (state, subscriptionVbid) => {
  if (subscriptionVbid) {
    const customMode = getSubscriptionBrandDisplayMode(state, subscriptionVbid);
    const assets = getAssets(state, utils.trendParams("all", subscriptionVbid));
    if (customMode) {
      return customMode;
    }
    if (assets.length && assets.length <= 20) {
      return "GRID";
    }
    if (isBrandedSubscription(state, subscriptionVbid)) {
      return "LANES";
    }
  }
  return undefined;
};
export const getBrandedSubscriptions = state => {
  if (!isUserFetching(state)) {
    try {
      return getUser(state).branding;
    } catch (e) {
      return undefined;
    }
  }
};
export const getBrandedSubscriptionVbids = state => {
  if (!isUserFetching(state)) {
    try {
      return Object.keys(getUser(state).branding);
    } catch (e) {
      return undefined;
    }
  }
};

const sumOfCreditsByStatus = (state, status) => {
  const creditList = getUserCredits(state);
  const values =
    creditList &&
    creditList.map(credit => {
      return credit.credits ? credit.credits[status] : 0;
    });
  const sum =
    values &&
    values.reduce((acc, curr) => {
      return curr !== -1 ? acc + curr : acc;
    }, 0);
  return sum;
};

export const getTotalUserCreditCount = state => {
  const credits = getUserCredits(state);
  return credits ? sumOfCreditsByStatus(state, "total") : 0;
};

export const getAvailableUserCreditCount = state => {
  const credits = getUserCredits(state);
  return credits ? sumOfCreditsByStatus(state, "available") : 0;
};
export const getUsedUserCreditCount = state => {
  const credits = getUserCredits(state);
  return credits ? sumOfCreditsByStatus(state, "used") : 0;
};

export const hasTotalUserCredits = state => {
  const credits = getUserCredits(state);
  const filteredCredits = credits.filter(c => {
    return utils.isUnlimitedUserCredit(c) || (c && c.credits.total > 0);
  });
  return filteredCredits.length > 0;
};

export const hasAvailableUserCredits = state => {
  return getAvailableUserCredits(state).length > 0;
};

export const getAvailableUserCredits = state => {
  const credits = getUserCredits(state);
  return credits.filter(utils.creditsAreAvailable);
};

export const getAvailableAssetCredits = (state, isbn) => {
  const asset = getAsset(state, isbn);
  if (!asset || !asset.creditList) {
    return [];
  }
  return asset.creditList.filter(utils.copiesAreAvailable);
};

export const getUserCreditById = (state, creditId) => {
  const credits = getUserCreditsById(state);
  return credits[creditId];
};

export const getAssetCreditById = (state, isbn, creditId) => {
  const credits = getAssetCredits(state, isbn);
  return credits.find(c => c.id === creditId);
};

export const getAvailableCreditIdsForAsset = (state, isbn) => {
  const availableUserCreditIds = getAvailableUserCredits(state).map(c => c.id);
  const availableAssetCreditIds = getAvailableAssetCredits(state, isbn).map(
    c => c.id
  );
  return intersection(availableUserCreditIds, availableAssetCreditIds);
};

export const getAvailableCreditsForAsset = (state, isbn) => {
  const ids = getAvailableCreditIdsForAsset(state, isbn);
  return ids.map(id => ({
    ...getUserCreditById(state, id),
    ...getAssetCreditById(state, isbn, id)
  }));
};

export const getBestCreditForAsset = (state, isbn) => {
  const matchingCredits = getAvailableCreditsForAsset(state, isbn);
  const firstUnlimitedMatchingCredit = matchingCredits.find(
    utils.isUnlimitedUserCredit
  );
  if (firstUnlimitedMatchingCredit !== undefined) {
    return firstUnlimitedMatchingCredit;
  }
  if (matchingCredits.length > 0) {
    return matchingCredits[0];
  }
  return null;
};
export const hasAvailableCopies = (state, isbn) => {
  const assetCreditsWithCopies = getAvailableAssetCredits(state, isbn);
  return assetCreditsWithCopies && assetCreditsWithCopies.length > 0;
};
// user has a credit available, copies may not be available
export const hasAvailableUserCreditsForAsset = (state, isbn) => {
  const availableUserCreditIds = getAvailableUserCredits(state).map(c => c.id);
  const allAssetCredits = getAssetCredits(state, isbn).map(c => c.id);
  return intersection(availableUserCreditIds, allAssetCredits).length > 0;
};

// returns the first user credit that is usable for this asset (used to show the used credit count)
export const getFirstCreditForAsset = (state, isbn) => {
  const allUserCreditIds = getUserCredits(state).map(c => c.id);
  const allAssetCreditIds = getAssetCredits(state, isbn).map(c => c.id);
  const matchingCreditIds = intersection(allUserCreditIds, allAssetCreditIds);
  const firstCredit = getUserCredits(state).find(
    c => c.id === matchingCreditIds[0]
  );
  return firstCredit;
};

// user has a credit available, and copies are available
export const isAvailableToBorrow = (state, isbn) => {
  const bestCredit = getBestCreditForAsset(state, isbn);
  return Boolean(bestCredit && bestCredit.id);
};
// this picks the 'best' user credit, then determines if that credit is unlimited or not
export const isCreditAsset = (state, isbn) => {
  const c = getBestCreditForAsset(state, isbn);
  return Boolean(c && !utils.isUnlimitedUserCredit(c));
};
// this picks the 'best' user credit, then determines if there's a limited number of books in that subscription
export const isConcurrencyAsset = (state, isbn) => {
  return !utils.isUnlimitedCopies(getBestCreditForAsset(state, isbn));
};
// credit is unlimited, book copies is unlimited
export const hasUnlimitedAccess = (state, isbn) => {
  return !isConcurrencyAsset(state, isbn) && !isCreditAsset(state, isbn);
};

export const getAvailableCopiesFromAllSubscriptions = (state, isbn) => {
  try {
    const asset = getAsset(state, isbn);
    const total = asset.copies.available;
    if (total === -1) {
      return "unlimited";
    } else {
      return total || 0;
    }
  } catch (e) {
    return 0;
  }
};

export const getTotalCopiesFromAllSubscriptions = (state, isbn) => {
  try {
    const asset = getAsset(state, isbn);
    const total = asset.copies.total;
    if (total === -1) {
      return "unlimited";
    } else {
      return total || 0;
    }
  } catch (e) {
    return 0;
  }
};

export const getTermsDuration = (state, isbn) => {
  const credit = getBestCreditForAsset(state, isbn);
  if (credit && credit.terms) {
    if (credit.terms.type === "numHrs") {
      return credit.terms.duration;
    } else if (credit.terms.type === "numDays") {
      return credit.terms.duration * 24;
    }
  }
  return undefined;
};
export const getTermsAbsDate = (state, isbn) => {
  const credit = getBestCreditForAsset(state, isbn);
  if (credit && credit.terms) {
    if (credit.terms.type === "absDate") {
      const endDate = new Date(credit.terms.date);
      return IntlUtil(endDate, "en-US");
    }
  }
  return undefined;
};

const isSingleAssetFetching = (state, isbn) => {
  // getAsset only returns full assets, so we need to pull manually
  const asset = state.bridge.assets.byId[isbn];
  if (asset) {
    return !!asset.isFetching;
  }
  return false;
};

export const isFetchAndActivatePending = (state, isbn) => {
  return (
    !getAsset(state, isbn) ||
    isSingleAssetFetching(state, isbn) ||
    (shouldAutoActivateAsset(state, isbn) &&
      (!isActive(state, isbn) || isActivating(state, isbn)))
  );
};

export const shouldAutoActivateAsset = (state, isbn) => {
  return (
    getBrandValue(state, "Bridge.autoActivate.mode", "always") === "always" &&
    !isActive(state, isbn) &&
    isAvailableToBorrow(state, isbn)
  );
};

export const getAncillariesForVbid = (state, isbn) => {
  try {
    const ancillariesObject = state.bridge.ancillaries[isbn];
    return ancillariesObject ? ancillariesObject.ancillaries || [] : [];
  } catch (e) {
    return [];
  }
};

export const isAncillariesFetching = (state, isbn) => {
  try {
    const ancillariesObject = state.bridge.ancillaries[isbn];
    return ancillariesObject ? ancillariesObject.isFetching : false;
  } catch (e) {
    return false;
  }
};

export const hasAncillariesForVbid = (state, isbn) => {
  const asset = getAsset(state, isbn);
  return asset.hasAncillaries;
};

export const shouldFetchAncillaries = (state, isbn) => {
  try {
    return !state.bridge.ancillaries[isbn];
  } catch (e) {
    return true;
  }
};

export const assetExpiringSoon = (state, isbn) => {
  const asset = getAsset(state, isbn);
  if (
    asset === undefined ||
    !asset.expirationDate ||
    asset.expirationDate === "never"
  ) {
    return false;
  }
  const currentDate = moment(new Date());
  const assetExpDate = moment(asset.expirationDate);
  const daysLeft = moment.duration(assetExpDate.diff(currentDate)).asDays();
  return daysLeft >= 0 && daysLeft <= 30;
};

export const isDistributable = (state, isbn) => {
  const asset = getAsset(state, isbn);
  try {
    return "distributable" in asset ? asset.distributable : true;
  } catch (e) {
    return true;
  }
};

export const getBookStoreResults = state => {
  return state.bridge.bookStore.results || [];
};

export const getBookStore = (state, isbn) => {
  const book = state.bridge.bookStore.results.find(book => book.vbid === isbn);
  return book && book.vbid ? book : undefined;
};

export const isSearchingBookStore = state => {
  return state.bridge.bookStore.isSearching;
};

export const getVitalsourceUrl = state => {
  try {
    const query = state.bridge.bookStore.query || "";
    const firstResultUrl = state.bridge.bookStore.results[0].storeUrl || "";
    const urlParts = firstResultUrl.split("textbooks");
    const storeUrl = urlParts[0];
    if (!query || !storeUrl) {
      return "";
    }
    const params = new URLSearchParams(urlParts[1]);
    params.set("term", query);
    return `${storeUrl}textbooks?${params.toString()}`;
  } catch (e) {
    return "";
  }
};

export const hasPromoSub = state => {
  const triggeringSubs = [
    "SUB-7804702992", // dev
    "SUB-7449856969" //prod
  ];

  return !!getUserSubscriptionVbids(state).find(userSub => {
    return triggeringSubs.indexOf(userSub) >= 0;
  });
};
